import { Component, OnInit, OnDestroy } from '@angular/core';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { BehaviorSubject, Observable, Subscription, of } from "rxjs";
import { tap, mergeMap, switchMap } from "rxjs/operators";
import { StratigraphicApplicationReportModel } from "../../../models/adhoc-reports.model";
import { AdhocReportsService } from "../../../services/adhoc-reports-service";
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { SubHeader } from '../../../../core/models/sub-header.model';
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { DateService } from '../../../../common/services/date.service';
import { ReportSearchCriteria } from '../../../models/admin-reports.model';
import { FinancialAssuranceService } from "../../../../common/services/financial-assurance.service";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";


@Component({
    selector: 'app-stratigraphic-wells-report',
    templateUrl: './stratigraphic-wells-report.component.html',
    styleUrls: ['./stratigraphic-wells-report.component.scss'],
    standalone: false
})
export class StratigraphicWellsReportComponent implements OnInit, OnDestroy {

    private data: BehaviorSubject<StratigraphicApplicationReportModel[]> = new BehaviorSubject<StratigraphicApplicationReportModel[]>(null);
    public data$: Observable<StratigraphicApplicationReportModel[]> = this.data.asObservable();

    excelStyles: any = this.formatter.excelStyles;

    fromDate: Date;
    toDate: Date;

    gridOptions: GridOptions;
	gridApi?: GridApi;
    get datesRequired(): boolean {
        return this.fromDate == null || this.toDate == null;
    }

    get invalidDates(): boolean {
        return this.fromDate > this.toDate && this.toDate != null && this.fromDate != null;
    }

    financialAssuranceTypes: any[] = null;
    wellTypes: any[] = null;


    private subheader: SubHeader = new SubHeader({ title: "Stratigraphic Applications", buttons: [{ name: "Run Report", method: () => this.runReport() }, { name: "Export", method: () => this.export() }] });

    subscription: Subscription = new Subscription();

    constructor(private dataService: AdhocReportsService, private subheaderService: SubHeaderService, private formatter: GridFormatterService, private dialog: DynamicDialogService,
        private dateService: DateService, private financialAssuranceService: FinancialAssuranceService, private domainService: DomainDefinitionService) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildColumns(), false, true, true, false);
		this.gridOptions.headerHeight = 75;
		this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
		};
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = new Date();
        this.runReport();
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    buildColumns(): ColDef[] {
        return [
            {
                headerClass: "wrapped",
                headerName: "Operator",
                headerTooltip: "Operator",
                field: "operator",
                tooltipField: "operator",
                minWidth: 150,
                maxWidth: 250,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams                
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "RICS Owner #",
                headerTooltip: "RICS Owner #",
                field: "organizationId",
                tooltipField: "organizationId",              
                minWidth: 125,
                maxWidth: 160,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "RBDMS Owner #",
                headerTooltip: "RBDMS Owner #",
                field: "operatorRBDMSNumber",
                tooltipField: "operatorRBDMSNumber",
                minWidth: 125,
                maxWidth: 160,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "stringType",
                headerName: "API #",
                headerTooltip: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                minWidth: 100,
                maxWidth: 150,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Stratigraphic Permit Issued",
                headerTooltip: "Date Stratigraphic Permit Issued",
                field: "dateStratigraphicPermitIssued",
                valueFormatter: this.formatter.formatDate("dateStratigraphicPermitIssued", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateStratigraphicPermitIssued", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 135,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Stratigraphic Permit Expires",
                headerTooltip: "Date Stratigraphic Permit Expires",
                field: "dateStratigraphicPermitExpires",
                valueFormatter: this.formatter.formatDate("dateStratigraphicPermitExpires", "MM/dd/yyyy","N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateStratigraphicPermitExpires", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date of Original Spud",
                headerTooltip: "Date of Original Spud",
                field: "dateOfOriginalSpud",
                valueFormatter: this.formatter.formatDate("dateOfOriginalSpud", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateOfOriginalSpud", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Date Total Depth Reached",
                headerTooltip: "Date Total Depth Reached",
                field: "dateTotalDepthReached",
                valueFormatter: this.formatter.formatDate("dateTotalDepthReached", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateTotalDepthReached", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Plugged",
                headerTooltip: "Date Plugged",
                field: "datePlugged",
                valueFormatter: this.formatter.formatDate("datePlugged", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("datePlugged", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Conversion Permit Submitted",
                headerTooltip: "Date Conversion Permit Submitted",
                field: "dateConversionPermitSubmitted",
                valueFormatter: this.formatter.formatDate("dateConversionPermitSubmitted", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateConversionPermitSubmitted", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Conversion Permit Well Type",
                headerTooltip: "Conversion Permit Well Type",
                field: "conversionPermitWellType",
                valueFormatter: this.wellTypeFormatter(),
                tooltipValueGetter: this.wellTypeFormatter(),
                minWidth: 100,
                maxWidth: 125,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Conversion Permit Issued",
                headerTooltip: "Date Conversion Permit Issued",
                field: "dateConversionPermitIssued",
                valueFormatter: this.formatter.formatDate("dateConversionPermitIssued", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateConversionPermitIssued", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Date Conversion Permit Expires",
                headerTooltip: "Date Conversion Permit Expires",
                field: "dateConversionPermitExpires",
                valueFormatter: this.formatter.formatDate("dateConversionPermitExpires", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateConversionPermitExpires", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Conversion Complete Date",
                headerTooltip: "Conversion Complete Date",
                field: "conversionCompleteDate",
                valueFormatter: this.formatter.formatDate("dateConversionPermitExpires", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("dateConversionPermitExpires", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "Financial Assurance Submitted Date",
                headerTooltip: "Financial Assurance Submitted Date",
                field: "financialAssuranceSubmittedDate",
                valueFormatter: this.formatter.formatDate("financialAssuranceSubmittedDate", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("financialAssuranceSubmittedDate", "MM/dd/yyyy", "N/A"),
                minWidth: 135,
                maxWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Financial Assurance Amount",
                headerTooltip: "Financial Assurance Amount",
                field: "financialAssuranceAmount",
                valueFormatter: this.formatter.formatCurrency("financialAssuranceAmount"),
                tooltipValueGetter: this.formatter.formatCurrency("financialAssuranceAmount"),
                minWidth: 125,
                maxWidth: 150,
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Financial Assurance Type",
                headerTooltip: "Financial Assurance Type",
                field: "financialAssuranceType",
                valueFormatter: this.financialAssuranceTypeFormatter(),
                tooltipValueGetter: this.financialAssuranceTypeFormatter(),
                minWidth: 150,
                maxWidth: 175,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,            
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "System Check Date 1",
                headerTooltip: "System Check Date 1",
                field: "systemCheckDate1",
                valueFormatter: this.formatter.formatDate("systemCheckDate1", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("systemCheckDate1", "MM/dd/yyyy", "N/A"),
                minWidth: 160,
                maxWidth: 185,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "System Check Date 2",
                headerTooltip: "System Check Date 2",
                field: "systemCheckDate2",
                valueFormatter: this.formatter.formatDate("systemCheckDate2", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("systemCheckDate2", "MM/dd/yyyy", "N/A"),
                minWidth: 160,
                maxWidth: 185,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                cellClass: "dateType",
                headerName: "System Check Date 3",
                headerTooltip: "System Check Date 3",
                field: "systemCheckDate3",
                valueFormatter: this.formatter.formatDate("systemCheckDate3", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("systemCheckDate3", "MM/dd/yyyy", "N/A"),
                minWidth: 160,
                maxWidth: 185,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams
            } as ColDef
        ];
    }

    export(): void {
        let fileName: string = `StratigraphicApplications_${this.dateService.datePipe.transform(this.fromDate, "MM/dd/yyyy")}_${this.dateService.datePipe.transform(this.toDate, "MM/dd/yyyy")}`;
        let sheetName: string = "Stratigraphic Applications";
        this.formatter.exportDataToExcel(fileName, sheetName, this.gridApi, false);
    }

    financialAssuranceTypeFormatter(): any {
        return (obj: any) => {
            let result: string = "";
            if (obj != null && obj.data != null && obj.data["financialAssuranceType"] != null) {
                result = obj.data["financialAssuranceType"];
                if (this.financialAssuranceTypes != null) {
                    let match = this.financialAssuranceTypes.find(f => f.id == result);
                    if (match != null) result = match.description;
                }
            }
            if (result == null || result.length == 0) result = "N/A";
            return result;
        }
    }

    fromDateChanged(evt: Date): void {
        this.fromDate = evt;
    }

    resizeColumns(): void {
		if (this.gridApi) {
            this.gridApi.autoSizeAllColumns();
        }
    }

    runReport(): void {
        if (!this.invalidDates && !this.datesRequired) {

            let finTypeObs$: Observable<any[]> = (this.financialAssuranceTypes == null)
                ? this.financialAssuranceService.TypeDescriptions.pipe(
                    tap((types: any[]) => {
                        this.financialAssuranceTypes = types;
                    }))
                : of(this.financialAssuranceTypes);
            let wellTypeObs$: Observable<any[]> = (this.wellTypes == null)
                ? this.domainService.getValues("WELL_TYP").pipe(
                    mergeMap((type1: any[]) => {
                        this.wellTypes = type1;
                        return this.domainService.getValues("WELL_TYPE").pipe(
                            tap((type2: any[]) => {
                                if (this.wellTypes == null) this.wellTypes = [];
                                this.wellTypes = this.wellTypes.concat(type2);
                            })
                        )
                    }
                ))
                : of(this.wellTypes);
            
            if (this.gridOptions != null && this.gridApi != null) {
                this.gridApi.setGridOption("loading", true);
            }
            let spec = new ReportSearchCriteria();
            spec.fromDate = this.fromDate;
            spec.toDate = this.toDate;
            let obs$: Observable<StratigraphicApplicationReportModel[]> = finTypeObs$.pipe(
                switchMap(() => {
                    return wellTypeObs$;
                }),
                switchMap(() => {                   
                    return this.dataService.getStratigraphicApplicationsReport(spec).pipe(
                        tap((rows: StratigraphicApplicationReportModel[]) => {
                            this.data.next(rows);
                        }))
                })
            );

            let subs$: Subscription = obs$.subscribe({
                next: (res: any) => {
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                },
                error: (error) => {
                    console.error(error);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                    this.dialog.showError();
                }
            });
            this.subscription.add(subs$);
        }

    }

    toDateChanged(evt: Date): void {
        this.toDate = evt;
    }

    wellTypeFormatter(): any {
        return (obj: any) => {
            let result: string = "";
            if (obj != null && obj.data != null && obj.data["conversionPermitWellType"] != null) {
                result = obj.data["conversionPermitWellType"];
                if (this.wellTypes != null) {
                    let match = this.wellTypes.find(t => t.Code == result);
                    if (match != null) result = match.Definition;
                }                
            }
            if (result == null || result.length == 0) result = "N/A";
            return result;
        }
    }
}
